/**
 * Marketplace pricing formulas. Pure functions only — same code runs on the
 * creator frontend (live preview) and on the server (authoritative snapshot).
 *
 * Pricing pipeline:
 *   fal.ai cost USD
 *     × exchangeRate (USD→RMB)      → RMB (float)
 *     × markupFactor                → RMB (float)
 *     ceil to cents                 → minimumRmbCents (int)
 *     × coinsPerRmb / 100           → priceCoins (int)
 *     reverse from priceCoins       → priceRmbCents (int)
 *
 * Consumer purchases use integer coins, so the displayed RMB snapshot is
 * aligned back to the coin price. Example at 10 coins/RMB:
 *   minimum ¥1.15 → 12 coins → displayed ¥1.20
 *
 * Creator share:
 *   cost USD × exchangeRate × creatorShareFactor
 *     ceil to cents                 → creatorShareCents (int)
 *
 * A bundle totals over each instruction's resolved cost.
 */

export interface MarketplaceRates {
  /** SystemSetting `exchangeRate.usdToRmb`. Example: 7.3 */
  exchangeRateUsdToRmb: number;
  /** SystemSetting `pricing.markupFactor`. Example: 3.0 */
  markupFactor: number;
  /** SystemSetting `pricing.creatorShareFactor`. Example: 1.5 */
  creatorShareFactor: number;
  /** SystemSetting `coinsPerRmb`. Default 10 */
  coinsPerRmb: number;
  /** SystemSetting `bundle.minPriceCoins`. Floor to prevent near-free bundles */
  minPriceCoins: number;
}

export interface PricingBreakdown {
  totalCostUsd: number;
  priceRmbCents: number;
  priceCoins: number;
  creatorShareCents: number;
}

export function computeBundlePricing(
  totalCostUsd: number,
  rates: MarketplaceRates,
): PricingBreakdown {
  const rmb = totalCostUsd * rates.exchangeRateUsdToRmb * rates.markupFactor;
  const minimumRmbCents = Math.max(
    Math.ceil(rmb * 100),
    Math.ceil((rates.minPriceCoins * 100) / rates.coinsPerRmb),
  );
  const priceCoins = Math.max(
    Math.ceil((minimumRmbCents * rates.coinsPerRmb) / 100),
    rates.minPriceCoins,
  );
  const priceRmbCents = Math.ceil((priceCoins * 100) / rates.coinsPerRmb);
  const creatorShareCents = Math.ceil(
    totalCostUsd * rates.exchangeRateUsdToRmb * rates.creatorShareFactor * 100,
  );
  return { totalCostUsd, priceRmbCents, priceCoins, creatorShareCents };
}

/**
 * Convert a single USD cost to RMB cents (ceil). Used for creator experiment
 * debits — no markup or floor, creator pays actual cost to the platform
 * converted to RMB. If you want a markup here, pass `markupFactor * USD`.
 */
export function usdToRmbCentsCeil(costUsd: number, exchangeRateUsdToRmb: number): number {
  return Math.ceil(costUsd * exchangeRateUsdToRmb * 100);
}

/**
 * Convert a RMB amount (in yuan, float) to cents (int). Used for topup flows.
 */
export function rmbYuanToCents(yuan: number): number {
  return Math.round(yuan * 100);
}
